import { CalendarDays, Minus, Plus } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { withErrorCode } from '@/lib/errors'
import { cn } from '@/lib/utils'
import { formatDateTime } from './ParticipantPackageSummary'
import type { ParticipantPerformanceOption } from '../types'

interface PerformanceAllocationProps {
  performances: ParticipantPerformanceOption[]
  includedTicketQuantity: number
  quantities: Record<string, number>
  onChange: (quantities: Record<string, number>) => void
  disabled?: boolean
  describedBy?: string
}

export function PerformanceAllocation({
  performances,
  includedTicketQuantity,
  quantities,
  onChange,
  disabled = false,
  describedBy,
}: PerformanceAllocationProps) {
  const total = getAllocationTotal(quantities)

  if (performances.length === 0) {
    return (
      <Alert variant="destructive">
        <CalendarDays className="h-4 w-4" aria-hidden="true" />
        <AlertDescription>
          {withErrorCode(
            'No performances are currently available for this invitation. Your response cannot be reserved until the production team updates the available capacity.',
            'participant-package/no-performances',
          )}
        </AlertDescription>
      </Alert>
    )
  }

  if (performances.length === 1) {
    return null
  }

  const totalIsValid = total === includedTicketQuantity
  const remaining = includedTicketQuantity - total

  return (
    <fieldset className="space-y-4" disabled={disabled} aria-describedby={describedBy}>
      <legend className="text-sm font-semibold">Choose your performance dates</legend>
      <p className="text-sm text-muted-foreground">
        Assign all {includedTicketQuantity} included ticket{includedTicketQuantity === 1 ? '' : 's'}.
        You may split them across performances.
      </p>

      <div className="grid gap-3">
        {performances.map((performance) => {
          const quantity = quantities[performance.performanceId] ?? 0
          const otherQuantity = total - quantity
          const maxForPerformance = Math.max(
            0,
            Math.min(performance.availableQuantity, includedTicketQuantity - otherQuantity)
          )
          const inputId = `performance-${performance.performanceId}`
          const availabilityId = `${inputId}-availability`

          return (
            <div
              key={performance.performanceId}
              className={cn(
                'rounded-lg border p-4 transition-colors',
                quantity > 0 ? 'border-primary/50 bg-primary/5' : 'bg-background'
              )}
            >
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <label htmlFor={inputId} className="cursor-pointer text-sm font-semibold">
                    {performance.title}
                  </label>
                  <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                    <CalendarDays className="h-4 w-4 shrink-0" aria-hidden="true" />
                    {formatDateTime(performance.startAt)}
                  </p>
                  <p id={availabilityId} className="mt-1 text-xs text-muted-foreground">
                    {formatAvailability(performance.availableQuantity)} available · {performance.venue}
                  </p>
                </div>

                <div className="flex items-center gap-2 self-start sm:self-center">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setQuantity(performance.performanceId, quantity - 1, maxForPerformance, quantities, onChange)}
                    disabled={quantity === 0 || disabled}
                    aria-label={`Remove one ticket from ${performance.title}`}
                  >
                    <Minus aria-hidden="true" />
                  </Button>
                  <input
                    id={inputId}
                    className="h-10 w-16 rounded-md border bg-background px-2 text-center text-sm font-semibold outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
                    type="number"
                    inputMode="numeric"
                    min={0}
                    max={maxForPerformance}
                    step={1}
                    value={quantity}
                    aria-label={`${performance.title} ticket quantity`}
                    aria-describedby={availabilityId}
                    onChange={(event) => {
                      const requested = Number.parseInt(event.target.value, 10)
                      setQuantity(
                        performance.performanceId,
                        Number.isFinite(requested) ? requested : 0,
                        maxForPerformance,
                        quantities,
                        onChange
                      )
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setQuantity(performance.performanceId, quantity + 1, maxForPerformance, quantities, onChange)}
                    disabled={quantity >= maxForPerformance || disabled}
                    aria-label={`Add one ticket to ${performance.title}`}
                  >
                    <Plus aria-hidden="true" />
                  </Button>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <div
        className={cn(
          'rounded-lg border px-4 py-3 text-sm',
          totalIsValid ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-amber-500/40 bg-amber-500/5'
        )}
        aria-live="polite"
      >
        <div className="flex items-center justify-between gap-3">
          <span className="font-medium">Tickets assigned</span>
          <span className="font-semibold">
            {total} / {includedTicketQuantity}
          </span>
        </div>
        {!totalIsValid && (
          <p className="mt-1 text-xs text-muted-foreground">
            {remaining > 0
              ? `Assign ${remaining} more ticket${remaining === 1 ? '' : 's'} to continue.`
              : `Remove ${Math.abs(remaining)} ticket${Math.abs(remaining) === 1 ? '' : 's'} to continue.`}
          </p>
        )}
      </div>
    </fieldset>
  )
}

export function getAllocationTotal(quantities: Record<string, number>): number {
  return Object.values(quantities).reduce(
    (total, quantity) => total + (Number.isFinite(quantity) ? Math.max(0, Math.trunc(quantity)) : 0),
    0
  )
}

function setQuantity(
  performanceId: string,
  requested: number,
  maxForPerformance: number,
  quantities: Record<string, number>,
  onChange: (quantities: Record<string, number>) => void
) {
  const nextQuantity = Math.max(0, Math.min(Math.trunc(requested), maxForPerformance))
  onChange({ ...quantities, [performanceId]: nextQuantity })
}

function formatAvailability(quantity: number): string {
  if (quantity <= 0) return 'No tickets currently available'
  return `${quantity} ticket${quantity === 1 ? '' : 's'}`
}
